import * as THREE from 'three';
import { kelvinToColor } from './lib/light-rigs';
import { enableEmissiveBloomLayer } from './lib/selectiveBloom';

type UnknownRecord = Record<string, unknown>;

function asRecord(value: unknown): UnknownRecord | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as UnknownRecord
    : null;
}

export type UnderCabinetLightingVisualContract = {
  color: THREE.Color;
  diffuserColor: string;
  washColor: string;
  intensity: number;
  washOpacity: number;
};

/**
 * The render-owned representation of a recognised under-cabinet LED.
 *
 * The canonical entity remains the billable aluminium fixture.  This object
 * adds only non-persistent children (diffuser, task light and backsplash
 * wash), so a visual cue cannot change the scene graph, BOM or exports.
 */
export class UnderCabinetLightingVisual {
  constructor(private readonly entityId: string) {}

  static contract(domain: unknown, render: unknown): UnderCabinetLightingVisualContract | null {
    const domainRecord = asRecord(domain);
    const renderRecord = asRecord(render);
    const lightingKind = String(domainRecord?.lightingKind ?? renderRecord?.lightingKind ?? '').trim();
    if (lightingKind !== 'under_cabinet') return null;
    const colorTemp = Math.max(1800, Math.min(6500, Number(domainRecord?.colorTemp ?? renderRecord?.colorTemp) || 3000));
    const color = kelvinToColor(colorTemp);
    return {
      color,
      diffuserColor: `#${color.getHexString()}`,
      washColor: colorTemp <= 3500 ? '#ff8a24' : `#${color.getHexString()}`,
      intensity: Math.max(4, Math.min(30, Number(domainRecord?.emissiveIntensity ?? renderRecord?.emissiveIntensity) * 7 || 18)),
      washOpacity: colorTemp <= 3500 ? 0.52 : 0.38,
    };
  }

  attach(mesh: THREE.Mesh, size: readonly [number, number, number], contract: UnderCabinetLightingVisualContract): void {
    const [sx, sy, sz] = size;
    const material = mesh.material;
    if (material instanceof THREE.MeshStandardMaterial || material instanceof THREE.MeshPhysicalMaterial) {
      material.color.copy(contract.color);
      material.emissive.copy(contract.color);
      material.emissiveIntensity = 4.4;
      material.toneMapped = false;
      material.roughness = 0.2;
    }

    const diffuser = new THREE.Mesh(
      new THREE.BoxGeometry(Math.max(1, sx * 0.98), Math.max(12, sy * 1.8), Math.max(18, sz * 1.5)),
      new THREE.MeshBasicMaterial({ color: contract.color, toneMapped: false }),
    );
    diffuser.name = `${this.entityId}::task-light-diffuser`;
    diffuser.position.set(0, -Math.max(6, sy * 0.75), 0);
    diffuser.userData.entityId = this.entityId;
    diffuser.userData.renderOnlyLightingCue = true;
    enableEmissiveBloomLayer(diffuser);
    mesh.add(diffuser);

    const taskLight = new THREE.PointLight(contract.color, contract.intensity, 1500, 2);
    taskLight.name = `${this.entityId}::task-light-source`;
    // Canonical cabinet fronts and the viewer's front camera face +Z.  The
    // former -Z placement put the source inside/behind a wall cabinet, so it
    // was present in the graph and BOM but invisible in the production view.
    taskLight.position.set(0, -45, Math.max(45, sz * 4));
    taskLight.castShadow = false;
    taskLight.userData.entityId = this.entityId;
    taskLight.userData.renderOnlyLightingCue = true;
    mesh.add(taskLight);

    const wash = new THREE.Mesh(
      new THREE.PlaneGeometry(Math.max(1, sx * 0.93), 260),
      new THREE.MeshBasicMaterial({
        color: contract.washColor,
        transparent: true,
        opacity: contract.washOpacity,
        depthWrite: false,
        side: THREE.DoubleSide,
        blending: THREE.NormalBlending,
        toneMapped: false,
      }),
    );
    wash.name = `${this.entityId}::task-light-wash`;
    // Project the source-verifiable warm cone towards the worktop/backsplash
    // on the visible (+Z) side of the cabinet, never through its back panel.
    wash.position.set(0, -150, Math.max(28, sz * 2.4));
    wash.renderOrder = 2;
    wash.userData.entityId = this.entityId;
    wash.userData.renderOnlyLightingCue = true;
    mesh.add(wash);

    mesh.castShadow = false;
    mesh.receiveShadow = false;
  }
}
